import { forwardRef, useCallback } from 'react';

import clsx from 'clsx';

import Backdrop from '@gogame/ui/Backdrop';
import Box from '@gogame/ui/Box';
import View from '@gogame/ui/View';
import Button from '@gogame/ui/Button';
import { Card, CardContent } from '@gogame/ui/Card';
import CircularProgress from '@gogame/ui/CircularProgress';
import Divider from '@gogame/ui/Divider';
import MuiFormControl, { useFormControl } from '@gogame/ui/FormControl';
import MuiFormHelperText from '@gogame/ui/FormHelperText';
import IconButton from '@gogame/ui/IconButton';
import Toolbar from '@gogame/ui/Toolbar';
import { makeStyles } from '@gogame/ui/styles';

import { BackspaceIcon } from '../Icons';
import MetricMenu from './MetricMenu';
import { Decimal, Default, Metric, CohortMetric } from './components';
import {
  TYPE_DECIMAL,
  TYPE_OPERATOR,
  TYPE_BRACKET_CLOSE,
  TYPE_BRACKET_OPEN,
  TYPE_METRIC,
  TYPE_COHORT_METRIC,
  OPERATOR_SUBTRACT,
  OPERATOR_ADD,
  OPERATOR_DIVIDE,
  OPERATOR_MULTIPLY,
  BRACKET_CLOSE,
  BRACKET_OPEN,
} from './constants';
import { useCustomMetricCalculator } from './useCustomMetricCalculator';

const useStyles = makeStyles((theme) => ({
  card: {
    position: 'relative',
  },
  charButton: {
    marginLeft: theme.spacing(1),
    borderRadius: '100%',
    padding: '0',
    minWidth: '40px',
    height: '40px',
    // fontSize: theme.typography.h5.fontSize,
    // lineHeight: theme.typography.h5.lineHeight
  },
  decimalButton: {
    marginLeft: theme.spacing(1),
  },
  metricMenu: {
    marginLeft: -theme.spacing(1.5),
  },
  cohortMetricsMenu: {
    marginLeft: theme.spacing(1),
  },
  container: {
    minHeight: '69px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    margin: 0,
    paddingTop: 16,
  },
  clearButton: {
    padding: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  error: {
    borderColor: theme.palette.error.main,
  },
}));

const CustomMetricCalculator = ({
  value,
  onChange,
  metrics = [],
  cohortMetrics = [],
  loading = false,
  renderOptionMetric,
}) => {
  const { addToken, editToken, removeToken, types, tokens } =
    useCustomMetricCalculator({
      value,
      onChange,
      metrics,
      cohortMetrics,
    });
  const classes = useStyles();
  const isDisabled = useCallback((type) => !types.includes(type), [types]);
  const formControl = useFormControl();

  return (
    <View loading={loading}>
      <Card
        className={clsx(classes.card, {
          [classes.error]: formControl.error,
        })}
        elevation={6}
      >
        <Box sx={{ bgcolor: 'action.hover' }}>
          <Toolbar>
            <MetricMenu
              edge={'start'}
              label={'Metric'}
              disabled={isDisabled(TYPE_METRIC)}
              metrics={metrics}
              groupBy={'groupName'}
              className={classes.metricMenu}
              onChange={(_e, value) => addToken({ type: TYPE_METRIC, value })}
              renderOption={renderOptionMetric}
            />
            {cohortMetrics.length > 0 ? (
              <MetricMenu
                className={classes.cohortMetricsMenu}
                label={'Cohort'}
                disabled={isDisabled(TYPE_COHORT_METRIC)}
                metrics={cohortMetrics}
                onChange={(_e, value) =>
                  addToken({
                    type: TYPE_COHORT_METRIC,
                    value: {
                      name: value,
                      dateRange: '0',
                    },
                  })
                }
              />
            ) : null}
            <Button
              color={'primary'}
              className={classes.decimalButton}
              onClick={() => addToken({ type: TYPE_DECIMAL, value: 1 })}
              disabled={isDisabled(TYPE_DECIMAL)}
            >
              123
            </Button>
            <Button
              color={'primary'}
              size={'large'}
              className={classes.charButton}
              onClick={() =>
                addToken({ type: TYPE_BRACKET_OPEN, value: BRACKET_OPEN })
              }
              disabled={isDisabled(TYPE_BRACKET_OPEN)}
            >
              (
            </Button>
            <Button
              color={'primary'}
              size={'large'}
              className={classes.charButton}
              onClick={() =>
                addToken({ type: TYPE_BRACKET_CLOSE, value: BRACKET_CLOSE })
              }
              disabled={isDisabled(TYPE_BRACKET_CLOSE)}
            >
              )
            </Button>
            <Button
              color={'primary'}
              size={'large'}
              className={classes.charButton}
              onClick={() =>
                addToken({ type: TYPE_OPERATOR, value: OPERATOR_ADD })
              }
              disabled={isDisabled(TYPE_OPERATOR)}
            >
              +
            </Button>
            <Button
              color={'primary'}
              size={'large'}
              className={classes.charButton}
              onClick={() =>
                addToken({ type: TYPE_OPERATOR, value: OPERATOR_SUBTRACT })
              }
              disabled={isDisabled(TYPE_OPERATOR)}
            >
              &mdash;
            </Button>
            <Button
              color={'primary'}
              size={'large'}
              className={classes.charButton}
              onClick={() =>
                addToken({ type: TYPE_OPERATOR, value: OPERATOR_MULTIPLY })
              }
              disabled={isDisabled(TYPE_OPERATOR)}
            >
              *
            </Button>
            <Button
              color={'primary'}
              size={'large'}
              className={classes.charButton}
              onClick={() =>
                addToken({ type: TYPE_OPERATOR, value: OPERATOR_DIVIDE })
              }
              disabled={isDisabled(TYPE_OPERATOR)}
            >
              /
            </Button>
            <IconButton
              color={'primary'}
              className={classes.clearButton}
              onClick={() => removeToken(tokens.length - 1)}
              disabled={!tokens.length}
            >
              <BackspaceIcon />
            </IconButton>
          </Toolbar>
        </Box>
        <Divider />
        <CardContent className={classes.container}>
          {!loading &&
            tokens.map((token, i) => {
              let element = null;
              switch (token.type) {
                case TYPE_DECIMAL:
                  element = (
                    <Decimal
                      value={token.value}
                      onChange={(_e, value) => editToken(i, value)}
                    />
                  );
                  break;
                case TYPE_METRIC:
                  element = (
                    <Metric
                      value={token.value}
                      onChange={(_e, value) => editToken(i, value)}
                      metrics={metrics}
                    />
                  );
                  break;
                case TYPE_COHORT_METRIC:
                  element = (
                    <CohortMetric
                      value={token.value}
                      onChange={(_e, value) => editToken(i, value)}
                      metrics={cohortMetrics}
                    />
                  );
                  break;
                default:
                  element = <Default value={token.value} />;
                  break;
              }

              return (
                <Box mr={2} mb={2} key={i}>
                  {element}
                </Box>
              );
            })}
        </CardContent>
      </Card>
    </View>
  );
};

const CustomMetricCalculatorField = forwardRef(
  ({ error, helper, ...props }, ref) => {
    const helperText = error || helper;
    return (
      <MuiFormControl error={!!error} variant={'outlined'} ref={ref}>
        <CustomMetricCalculator {...props} />
        {helperText ? (
          <MuiFormHelperText>{helperText}</MuiFormHelperText>
        ) : null}
      </MuiFormControl>
    );
  }
);

export default CustomMetricCalculatorField;
